import { type Ref, Fragment, nextTick, ref, type ComputedRef, defineComponent, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CBulkDeleteErrors, {
  type CBulkDeleteErrorDetails,
} from '@lib/components/CBulkDeleteErrors/CBulkDeleteErrors.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CConfigEditor from '@lib/components/CConfigEditor/CConfigEditor.vue'
import CAttributesForm, { type Form as AttributesForm } from '@lib/components/CAttributesForm/CAttributesForm.vue'
import CFileAttributes, { type Form as FileAttributesForm } from '@lib/components/CFileAttributes/CFileAttributes.vue'
import { NCollapse, NCollapseItem, NSelect, type SelectOption } from 'naive-ui'

import useConfirmation from '@lib/composables/useConfirmation'
import useToast from '@lib/composables/useToast'
import useThemeVars from '@lib/composables/useThemeVars'
import useBulkActions from '@lib/composables/bulkActions.use'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import type { ConfigForm } from '@lib/components/CConfigEditor/CConfigEditor.types'
import { type CanvasType } from '@lib/types/canvases'
import type {
  PaginatedMetadataTemplateList,
  CanvasRetrieve,
  PatchedCanvasUpdateRequest,
  ContentDistributionGroup,
  PaginatedContentDistributionGroupList,
} from '@openapi'
import type { PitcherInfo } from '@lib/types/app'
import { inject } from 'vue'
import { useCoreApi } from '@lib/composables/useCoreApi'
import type { AxiosInstance } from 'axios'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

interface BaseBulkProps {
  selectedRows: Ref<Record<string, boolean>>
  title?: string
  onComplete: () => void | Promise<void>
  confirmText?: string
  cancelText?: string
}
interface BulkUpdateFileAttributeProps extends BaseBulkProps {
  fileNames: string[]
  metadataFetcher?: (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
  noMetadataErrorText?: string
  subtitle?: string
  isBulkUpdating: Ref<boolean>
}
interface BulkUpdateMetadataDialogProps extends BaseBulkProps {
  selectedItemNames?: string[]
  metadataFetcher?: (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
  noMetadataErrorText?: string
  subtitle?: string
  isBulkUpdating: Ref<boolean>
  canvasType: CanvasType
  updater?: (
    params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
  ) => Promise<CanvasRetrieve>
}
interface BulkDeleteDialogProps extends BaseBulkProps {
  filteredData: ComputedRef<CanvasRetrieve[]>
  isBulkDeleting: Ref<boolean>
  canvasType: CanvasType
  isReadonly?: (canvas: CanvasRetrieve) => boolean
  content?: string
}
interface BulkTagsProps extends BaseBulkProps {
  filteredData: ComputedRef<CanvasRetrieve[]>
  isBulkUpdating: Ref<boolean>
  canvasType: CanvasType
  isReadonly?: (canvas: CanvasRetrieve) => boolean
  content?: string
}

export default function useCanvasesManagementBulk() {
  const confirmation = useConfirmation()
  const themeVars = useThemeVars()
  const { toast } = useToast()
  const { t } = useI18n()
  const { coreRestApiAxios } = useCoreApi()

  const { fetchSuggestedInstanceTags } = useSuggestedTags()
  const { deleteCanvases, mergeMetadata, addTags, removeTags } = useBulkActions()

  const pitcherInfo = inject<Ref<PitcherInfo>>('pitcherInfo')
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )
  const distributionGroups = ref<ContentDistributionGroup[]>([])

  async function fetchDistributionGroups() {
    if (!restApiAxios?.value || !pitcherInfo?.value?.instanceId) {
      return
    }

    try {
      const response = await restApiAxios.value.get<unknown, PaginatedContentDistributionGroupList>(
        'content-distribution-groups/',
        {
          params: {
            instance_id: pitcherInfo.value.instanceId,
          },
        },
      )

      if (!response?.results?.length) {
        return
      }

      distributionGroups.value = response.results
    } catch (error) {
      console.error('Failed to fetch distribution groups:', error)
      toast.error(t('canvasUI.components.bulkUpdateDistribution.fetchGroupsError'))
    }
  }

  function getCanvasTypeTranslation(entity: CanvasType | 'file', count: number) {
    let translatedEntity = ''

    switch (entity) {
      case 'canvas':
        translatedEntity = t('canvasUI.common.canvas', { count })
        break
      case 'file':
        translatedEntity = t('canvasUI.common.file', { count })
        break
      case 'section':
        translatedEntity = t('canvasUI.common.section', { count })
        break
      case 'template':
        translatedEntity = t('canvasUI.common.template', { count })
        break
      case 'section-template':
        translatedEntity = t('canvasUI.common.sectionTemplate', { count })
        break
    }

    return translatedEntity.toLocaleLowerCase()
  }

  async function bulkErrorDialog(
    errorsDetails: CBulkDeleteErrorDetails[],
    title: string,
    { confirmText }: { confirmText?: string } = {},
  ) {
    await nextTick()
    confirmation.error({
      style: { width: 'auto' },
      hasExit: false,
      hasCancel: false,
      title,
      content: () => <CBulkDeleteErrors errorsDetails={errorsDetails} />,
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
    })
  }

  async function bulkUpdateFileAttributes({
    selectedRows,
    fileNames,
    metadataFetcher,
    noMetadataErrorText,
    title,
    subtitle,
    isBulkUpdating,
    onComplete,
    confirmText,
    cancelText,
  }: BulkUpdateFileAttributeProps) {
    const selectedIds = getSelectedIds(selectedRows)
    const instanceMetadataTemplate = await fetchMetadataTemplate(metadataFetcher, noMetadataErrorText)

    if (!instanceMetadataTemplate) return

    const configEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)
    const formData = ref<FileAttributesForm>(createInitialFileAttributesFormData())

    await showFileAttributesConfirmationDialog({
      title,
      subtitle,
      formData,
      configEditorRef,
      instanceMetadataTemplate,
      isBulkUpdating,
      selectedIds,
      fileNames,
      onComplete,
      confirmText,
      cancelText,
    })
  }

  function getSelectedIds(selectedRows: Ref<Record<string, boolean>>): string[] {
    return Object.entries(selectedRows.value)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id)
  }

  async function fetchMetadataTemplate(
    metadataFetcher?: (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>,
    noMetadataErrorText?: string,
  ): Promise<ConfigForm | null> {
    const metaDataResponse = await metadataFetcher?.()
    const results = metaDataResponse?.results ?? []

    const instanceMetadataTemplate = results[0]
    if (!instanceMetadataTemplate?.sections?.length) {
      toast.warning(noMetadataErrorText ?? t('canvasUI.components.bulkUpdateFileAttributes.noMetadataError'))
      return null
    }

    return instanceMetadataTemplate as ConfigForm
  }

  function createInitialFileAttributesFormData(): FileAttributesForm {
    return {
      permissions: {
        can_share: undefined,
      },
      tags: [],
      expiresAtDateTime: undefined,
      expiresAtOption: undefined,
      accessType: undefined,
      collaborations: [],
    }
  }

  async function showFileAttributesConfirmationDialog({
    title,
    subtitle,
    formData,
    configEditorRef,
    instanceMetadataTemplate,
    isBulkUpdating,
    selectedIds,
    fileNames,
    onComplete,
    confirmText,
    cancelText,
  }: {
    title?: string
    subtitle?: string
    formData: Ref<FileAttributesForm>
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>
    instanceMetadataTemplate: ConfigForm
    isBulkUpdating: Ref<boolean>
    selectedIds: string[]
    fileNames: string[]
    onComplete: () => void | Promise<void>
    confirmText?: string
    cancelText?: string
  }) {
    confirmation.create({
      style: { width: '50%' },
      hasExit: false,
      title: createFileAttributesDialogTitle(title, subtitle, selectedIds, fileNames),
      content: createFileAttributesDialogContent(formData, configEditorRef, instanceMetadataTemplate, isBulkUpdating),
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: () => handleFileAttributesConfirm(configEditorRef, formData, selectedIds, isBulkUpdating, onComplete),
    })
  }

  function createFileAttributesDialogTitle(
    title: string | undefined,
    subtitle: string | undefined,
    selectedIds: string[],
    fileNames: string[],
  ) {
    return () => (
      <div>
        <div class="flex items-center gap-2">
          <span>
            {title ??
              t('canvasUI.components.bulkUpdateFileAttributes.title', {
                count: selectedIds.length,
                entity: getCanvasTypeTranslation('file', selectedIds.length),
              })}
          </span>
          <CHelpText>
            <div>
              <ul class="ml-4">
                {fileNames.map((fileName) => (
                  <li>{fileName}</li>
                ))}
              </ul>
            </div>
          </CHelpText>
        </div>
        <div class="font-size-4" style={{ color: themeVars.value.text2 }}>
          {subtitle ?? t('canvasUI.components.bulkUpdateFileAttributes.subtitle')}
        </div>
      </div>
    )
  }

  function createFileAttributesDialogContent(
    formData: Ref<FileAttributesForm>,
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>,
    instanceMetadataTemplate: ConfigForm,
    isBulkUpdating: Ref<boolean>,
  ) {
    return () => (
      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          paddingRight: '8px',
        }}
      >
        <NCollapse defaultExpandedNames={['properties', 'metadata']} displayDirective="show">
          <NCollapseItem name="properties" title={t('canvasUI.browserApp.uploadFilesModal.properties')}>
            <CFileAttributes
              showExpirationSettings={false}
              showPermissionSettings={false}
              showAccessTypeSettings={false}
              showKeepAsIsPlaceholder={true}
              modelValue={formData.value}
              onUpdate:modelValue={(newValue) => {
                formData.value = newValue
              }}
            />
          </NCollapseItem>
          <NCollapseItem name="metadata" title={t('canvasUI.browserApp.uploadFilesModal.metadata')}>
            <CConfigEditor
              ref={configEditorRef}
              loading={isBulkUpdating.value}
              template={instanceMetadataTemplate}
              showKeepAsIsPlaceholder={true}
            />
          </NCollapseItem>
        </NCollapse>
      </div>
    )
  }

  async function handleFileAttributesConfirm(
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>,
    formData: Ref<FileAttributesForm>,
    selectedIds: string[],
    isBulkUpdating: Ref<boolean>,
    onComplete: () => void | Promise<void>,
  ) {
    isBulkUpdating.value = true

    const { success, config } = await configEditorRef.value!.validate()
    if (!success) {
      isBulkUpdating.value = false
      return false
    }

    const promises = []

    if (formData.value.tagUpdateOperation === 'add' && formData.value.tags?.length) {
      promises.push(addTags({ file_ids: selectedIds, tags: formData.value.tags }))
    } else if (formData.value.tagUpdateOperation === 'remove' && formData.value.tags?.length) {
      promises.push(removeTags({ file_ids: selectedIds, tags: formData.value.tags }))
    }

    if (Object.keys(config).length > 0) {
      const updatedConfig = Object.fromEntries(Object.entries(config).filter(([, value]) => value !== null))
      promises.push(mergeMetadata({ file_ids: selectedIds, metadata: updatedConfig }))
    }

    if (promises.length === 0) {
      isBulkUpdating.value = false
      return
    }

    try {
      const results = await Promise.allSettled(promises)

      const totalSuccessCount = results[0].status === 'fulfilled' ? results[0].value.files.length : 0
      let hasError = false

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          hasError = true
          console.error('Operation failed:', result.reason)
        }
      })

      if (totalSuccessCount > 0) {
        toast.success(
          t('canvasUI.components.bulkUpdateFileAttributes.successMsg', {
            entity: getCanvasTypeTranslation('file', totalSuccessCount),
            count: totalSuccessCount,
          }),
        )
      }

      if (hasError) {
        toast.error(t('canvasUI.components.bulkUpdateFileAttributes.errorMsg'))
      }
    } catch (error) {
      toast.error(t('canvasUI.components.bulkUpdateFileAttributes.errorMsg'))
      console.error('Error in bulk update:', error)
    } finally {
      isBulkUpdating.value = false
      await onComplete()
    }
  }

  function createInitialMetadataFormData(): AttributesForm {
    return {
      is_draft: undefined,
    }
  }

  async function bulkUpdateMetadataDialog({
    selectedRows,
    selectedItemNames = [],
    metadataFetcher,
    noMetadataErrorText,
    title,
    subtitle,
    isBulkUpdating,
    onComplete,
    confirmText,
    cancelText,
    canvasType,
    updater,
  }: BulkUpdateMetadataDialogProps) {
    const selectedIds = getSelectedIds(selectedRows)
    const instanceMetadataTemplate = await fetchMetadataTemplate(metadataFetcher, noMetadataErrorText)

    if (!instanceMetadataTemplate) return

    const configEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)
    const formData = ref<AttributesForm>(createInitialMetadataFormData())

    await showMetadataConfirmationDialog({
      title,
      subtitle,
      formData,
      configEditorRef,
      instanceMetadataTemplate,
      isBulkUpdating,
      selectedIds,
      selectedItemNames,
      canvasType,
      onComplete,
      confirmText,
      cancelText,
      updater,
    })
  }

  async function showMetadataConfirmationDialog({
    title,
    subtitle,
    formData,
    configEditorRef,
    instanceMetadataTemplate,
    isBulkUpdating,
    selectedIds,
    selectedItemNames,
    canvasType,
    onComplete,
    confirmText,
    cancelText,
    updater,
  }: {
    title?: string
    subtitle?: string
    formData: Ref<AttributesForm>
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>
    instanceMetadataTemplate: ConfigForm
    isBulkUpdating: Ref<boolean>
    selectedIds: string[]
    selectedItemNames: string[]
    canvasType: CanvasType
    onComplete: () => void | Promise<void>
    confirmText?: string
    cancelText?: string
    updater?: (
      params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
    ) => Promise<CanvasRetrieve>
  }) {
    confirmation.create({
      style: { width: '50%' },
      hasExit: false,
      title: createMetadataDialogTitle(title, subtitle, selectedIds, selectedItemNames, canvasType),
      content: createMetadataDialogContent(formData, configEditorRef, instanceMetadataTemplate, isBulkUpdating),
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: () =>
        handleMetadataConfirm(configEditorRef, formData, selectedIds, isBulkUpdating, onComplete, canvasType, updater),
    })
  }

  function createMetadataDialogTitle(
    title: string | undefined,
    subtitle: string | undefined,
    selectedIds: string[],
    selectedItemNames: string[],
    canvasType: CanvasType,
  ) {
    return () => (
      <div>
        <div class="flex items-center gap-2">
          <span>
            {title ??
              t('canvasUI.components.bulkUpdateFileAttributes.title', {
                count: selectedIds.length,
                entity: getCanvasTypeTranslation(canvasType, selectedIds.length),
              })}
          </span>
          <CHelpText>
            <div>
              <ul class="ml-4">
                {selectedItemNames.map((itemName) => (
                  <li>{itemName}</li>
                ))}
              </ul>
            </div>
          </CHelpText>
        </div>
        <div class="font-size-4" style={{ color: themeVars.value.text2 }}>
          {subtitle ?? t('canvasUI.components.bulkUpdateMetadata.subtitle')}
        </div>
      </div>
    )
  }

  function createMetadataDialogContent(
    formData: Ref<AttributesForm>,
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>,
    instanceMetadataTemplate: ConfigForm,
    isBulkUpdating: Ref<boolean>,
  ) {
    // Check if we're in the admin interface using pitcherInfo platform
    const isAdminInterface = computed(() => pitcherInfo?.value?.platform === 'admin')

    // Metadata is read-only when NOT in admin interface AND the flag is false
    const isMetadataReadOnly = computed(
      () => !isAdminInterface.value && !launchDarkly.value.allow_rep_canvas_metadata_edit,
    )

    return () => (
      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          paddingRight: '8px',
        }}
      >
        <NCollapse defaultExpandedNames={['properties', 'metadata']} displayDirective="show">
          <NCollapseItem name="properties" title={t('canvasUI.browserApp.uploadFilesModal.properties')}>
            <CAttributesForm
              modelValue={formData.value}
              showKeepAsIsPlaceholder={true}
              onUpdate:modelValue={(newValue) => {
                formData.value = newValue
              }}
            />
          </NCollapseItem>
          <NCollapseItem name="metadata" title={t('canvasUI.browserApp.uploadFilesModal.metadata')}>
            <CConfigEditor
              ref={configEditorRef}
              loading={isBulkUpdating.value}
              readOnly={isMetadataReadOnly.value}
              template={instanceMetadataTemplate}
              showKeepAsIsPlaceholder={!isMetadataReadOnly.value}
            />
          </NCollapseItem>
        </NCollapse>
      </div>
    )
  }

  async function handleMetadataConfirm(
    configEditorRef: Ref<null | InstanceType<typeof CConfigEditor>>,
    formData: Ref<AttributesForm>,
    selectedIds: string[],
    isBulkUpdating: Ref<boolean>,
    onComplete: () => void | Promise<void>,
    canvasType: CanvasType,
    updater?: (
      params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
    ) => Promise<CanvasRetrieve>,
  ) {
    isBulkUpdating.value = true

    const { success, config } = await configEditorRef.value!.validate()

    if (!success) {
      isBulkUpdating.value = false
      return false
    }

    try {
      const promises = []

      if (Object.keys(config).length > 0) {
        promises.push(mergeMetadata({ canvas_ids: selectedIds, metadata: config }))
      }

      if (updater && formData.value.is_draft !== undefined) {
        promises.push(
          Promise.all(
            selectedIds.map((id) =>
              updater({
                id,
                is_draft: formData.value.is_draft,
                fields: 'is_draft',
              }),
            ),
          ),
        )
      }

      if (promises.length === 0) {
        isBulkUpdating.value = false
        return
      }

      const results = await Promise.allSettled(promises)

      let totalSuccessCount = 0
      let hasError = false

      // Find metadata result if it exists
      if (Object.keys(config).length > 0) {
        const metadataResult = results.find(
          (r) => r.status === 'fulfilled' && ('canvases' in r.value || Array.isArray(r.value)),
        )
        if (metadataResult?.status === 'fulfilled') {
          totalSuccessCount = Array.isArray(metadataResult.value)
            ? metadataResult.value.length
            : (metadataResult.value.canvases?.length ?? 0)
        }
      }

      // Find status update result if it exists
      if (updater && formData.value.is_draft !== undefined) {
        const statusResult = results.find((r) => r.status === 'fulfilled' && Array.isArray(r.value))
        if (statusResult?.status === 'fulfilled') {
          if (Object.keys(config).length === 0) {
            totalSuccessCount = (statusResult.value as CanvasRetrieve[]).length
          } else if ((statusResult.value as CanvasRetrieve[]).length !== totalSuccessCount) {
            hasError = true
          }
        }
      }

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          hasError = true
          console.error('Operation failed:', result.reason)
        }
      })

      if (totalSuccessCount > 0) {
        toast.success(
          t('canvasUI.components.bulkUpdateMetadata.successMsg', {
            entity: getCanvasTypeTranslation(canvasType, totalSuccessCount),
            count: totalSuccessCount,
          }),
        )
      }

      if (hasError) {
        toast.error(t('canvasUI.components.bulkUpdateMetadata.errorMsg'))
      }
    } catch (error) {
      toast.error(t('canvasUI.components.bulkUpdateMetadata.errorMsg'))
      console.error('Error in bulk metadata update:', error)
    } finally {
      isBulkUpdating.value = false
      await onComplete()
    }
  }

  async function bulkDeleteDialog({
    filteredData,
    selectedRows,
    isBulkDeleting,
    canvasType,
    onComplete,
    isReadonly = () => false,
    title,
    content,
    confirmText,
    cancelText,
  }: BulkDeleteDialogProps) {
    const selectedRowsCount = Object.entries(selectedRows.value).filter(([, isSelected]) => isSelected).length
    const translatedEntity = getCanvasTypeTranslation(canvasType, selectedRowsCount)

    confirmation.danger({
      hasExit: false,
      title: title ?? t('canvasUI.components.bulkDelete.title'),
      content:
        content ??
        t('canvasUI.components.bulkDelete.content', {
          count: selectedRowsCount,
          entity: translatedEntity,
        }),
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: async () => {
        isBulkDeleting.value = true
        const canvasLookup = new Map(filteredData.value.map((canvas) => [canvas.id, canvas]))

        const { deletableCanvases, readonlyErrors } = Object.entries(selectedRows.value)
          .filter(([, isSelected]) => isSelected)
          .reduce(
            (acc, [id]) => {
              const canvas = canvasLookup.get(id)
              if (canvas) {
                const isReadonlyCanvas = isReadonly(canvas)
                const errorDetails: CBulkDeleteErrorDetails = {
                  id,
                  name: canvas.name,
                  error_message: isReadonlyCanvas
                    ? t('canvasUI.components.bulkDelete.permissionErrorMessage', {
                        entity: getCanvasTypeTranslation(canvasType, 1),
                      })
                    : '',
                }
                if (isReadonlyCanvas) {
                  acc.readonlyErrors.push({ ...errorDetails, error: 'permission_denied' }) //Note: should not be translated, should be the same as the backend error
                } else {
                  acc.deletableCanvases.push({ id, name: canvas.name })
                }
              }
              return acc
            },
            {
              deletableCanvases: [] as { id: string; name: string }[],
              readonlyErrors: [] as CBulkDeleteErrorDetails[],
            },
          )

        if (deletableCanvases.length === 0) {
          bulkErrorDialog(
            readonlyErrors,
            t('canvasUI.components.bulkDelete.errorTitle', {
              entity: getCanvasTypeTranslation(canvasType, readonlyErrors.length),
              count: readonlyErrors.length,
            }),
          )
          isBulkDeleting.value = false
          return
        }

        deleteCanvases({ ids: deletableCanvases.map((c) => c.id) })
          .then(({ deleted_ids, errors }) => {
            if (deleted_ids.length > 0) {
              toast.success(
                t('canvasUI.components.bulkDelete.successMsg', {
                  entity: getCanvasTypeTranslation(canvasType, deleted_ids.length),
                  count: deleted_ids.length,
                }),
              )
            }

            const deletionErrors = errors.map((error) => {
              const canvas = canvasLookup.get(error.id)
              return {
                id: error.id,
                name: canvas ? canvas.name : '-',
                error_message: error.error_message,
              }
            })

            const combinedErrors = [...deletionErrors, ...readonlyErrors]

            if (combinedErrors.length > 0) {
              bulkErrorDialog(
                combinedErrors,
                t('canvasUI.components.bulkDelete.errorTitle', {
                  entity: getCanvasTypeTranslation(canvasType, combinedErrors.length),
                  count: combinedErrors.length,
                }),
              )
            }
          })
          .finally(async () => {
            isBulkDeleting.value = false
            await onComplete()
          })
      },
    })
  }

  async function bulkAddTagsDialog({
    filteredData,
    selectedRows,
    isBulkUpdating,
    canvasType,
    onComplete,
    isReadonly = () => false,
    title,
    confirmText,
    cancelText,
  }: BulkTagsProps) {
    const selectedRowsCount = Object.entries(selectedRows.value).filter(([, isSelected]) => isSelected).length
    const canvasLookup = new Map(filteredData.value.map((canvas) => [canvas.id, canvas]))

    const { editableCanvases, readonlyErrors } = Object.entries(selectedRows.value)
      .filter(([, isSelected]) => isSelected)
      .reduce(
        (acc, [id]) => {
          const canvas = canvasLookup.get(id)
          if (canvas) {
            const isReadonlyCanvas = isReadonly(canvas)
            const errorDetails: CBulkDeleteErrorDetails = {
              id,
              name: canvas.name,
              error_message: isReadonlyCanvas
                ? t('canvasUI.components.bulkAddTags.permissionErrorMessage', {
                    entity: getCanvasTypeTranslation(canvasType, 1),
                  })
                : '',
            }
            if (isReadonlyCanvas) {
              acc.readonlyErrors.push({ ...errorDetails, error: 'permission_denied' }) //Note: should not be translated, should be the same as the backend error
            } else {
              acc.editableCanvases.push({ id, name: canvas.name })
            }
          }
          return acc
        },
        {
          editableCanvases: [] as { id: string; name: string }[],
          readonlyErrors: [] as CBulkDeleteErrorDetails[],
        },
      )

    const tags: Ref<string[]> = ref([])
    confirmation.create({
      style: { width: '50%' },
      hasExit: false,
      title:
        title ??
        t('canvasUI.components.bulkAddTags.title', {
          entity: getCanvasTypeTranslation(canvasType, selectedRowsCount),
          count: selectedRowsCount,
        }),
      content: () => (
        <CTags
          value={tags.value}
          suggestedTagsFetcher={
            canvasType !== 'section-template'
              ? (search) =>
                  fetchSuggestedInstanceTags(search, {
                    type: ['canvases'],
                    canvasType: canvasType,
                  })
              : undefined
          }
          onUpdate:value={(newValue) => {
            tags.value = newValue
          }}
        />
      ),
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: async () => {
        isBulkUpdating.value = true

        if (tags.value.length === 0) {
          isBulkUpdating.value = false
          return
        }

        if (editableCanvases.length === 0) {
          bulkErrorDialog(
            readonlyErrors,
            t('canvasUI.components.bulkAddTags.errorTitle', {
              entity: getCanvasTypeTranslation(canvasType, readonlyErrors.length),
              count: readonlyErrors.length,
            }),
          )
          isBulkUpdating.value = false
          return
        }

        addTags({ canvas_ids: editableCanvases.map((c) => c.id), tags: tags.value })
          .then(({ canvases }) => {
            if (canvases.length > 0) {
              toast.success(
                t('canvasUI.components.bulkAddTags.successMsg', {
                  entity: getCanvasTypeTranslation(canvasType, canvases.length),
                  count: canvases.length,
                }),
              )
            }

            if (readonlyErrors.length > 0) {
              bulkErrorDialog(
                readonlyErrors,
                t('canvasUI.components.bulkAddTags.errorTitle', {
                  entity: getCanvasTypeTranslation(canvasType, readonlyErrors.length),
                  count: readonlyErrors.length,
                }),
              )
            }
          })
          .finally(async () => {
            isBulkUpdating.value = false
            await onComplete()
          })
      },
    })
  }

  async function bulkRemoveTagsDialog({
    filteredData,
    selectedRows,
    isBulkUpdating,
    canvasType,
    onComplete,
    isReadonly = () => false,
    title,
    confirmText,
    cancelText,
  }: BulkTagsProps) {
    const selectedRowsCount = Object.entries(selectedRows.value).filter(([, isSelected]) => isSelected).length
    const canvasLookup = new Map(filteredData.value.map((canvas) => [canvas.id, canvas]))

    const { editableCanvases, readonlyErrors, uniqueTags } = Object.entries(selectedRows.value)
      .filter(([, isSelected]) => isSelected)
      .reduce(
        (acc, [id]) => {
          const canvas = canvasLookup.get(id)
          if (canvas) {
            const isReadonlyCanvas = isReadonly(canvas)
            const errorDetails: CBulkDeleteErrorDetails = {
              id,
              name: canvas.name,
              error_message: isReadonlyCanvas
                ? t('canvasUI.components.bulkRemoveTags.permissionErrorMessage', {
                    entity: getCanvasTypeTranslation(canvasType, 1),
                  })
                : '',
            }
            if (isReadonlyCanvas) {
              acc.readonlyErrors.push({ ...errorDetails, error: 'permission_denied' }) //Note: should not be translated, should be the same as the backend error
            } else {
              acc.editableCanvases.push({ id, name: canvas.name })

              if (canvas.tags) {
                canvas.tags.forEach((tag) => acc.uniqueTags.add(tag))
              }
            }
          }
          return acc
        },
        {
          editableCanvases: [] as { id: string; name: string }[],
          readonlyErrors: [] as CBulkDeleteErrorDetails[],
          uniqueTags: new Set<string>(),
        },
      )

    const getUniqueSortedTags = () => Array.from(uniqueTags).sort((a, b) => a.localeCompare(b))

    const tags: Ref<string[]> = ref([])
    confirmation.create({
      style: { width: '50%' },
      hasExit: false,
      title:
        title ??
        t('canvasUI.components.bulkRemoveTags.title', {
          entity: getCanvasTypeTranslation(canvasType, selectedRowsCount),
          count: selectedRowsCount,
        }),
      content: () => (
        <CTags
          value={tags.value}
          disableCreateTag={true}
          suggestedTagsFetcher={getUniqueSortedTags}
          suggestedTagsTitle={t('canvasUI.components.bulkRemoveTags.suggestedTagsTitle')}
          showSuggestedTags={true}
          minCharsToSuggest={0}
          onUpdate:value={(newValue) => {
            tags.value = newValue
          }}
        />
      ),
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: async () => {
        isBulkUpdating.value = true

        if (tags.value.length === 0) {
          isBulkUpdating.value = false
          return
        }

        if (editableCanvases.length === 0) {
          bulkErrorDialog(
            readonlyErrors,
            t('canvasUI.components.bulkRemoveTags.errorTitle', {
              entity: getCanvasTypeTranslation(canvasType, readonlyErrors.length),
              count: readonlyErrors.length,
            }),
          )
          isBulkUpdating.value = false
          return
        }

        removeTags({ canvas_ids: editableCanvases.map((c) => c.id), tags: tags.value })
          .then(({ canvases }) => {
            if (canvases.length > 0) {
              toast.success(
                t('canvasUI.components.bulkRemoveTags.successMsg', {
                  entity: getCanvasTypeTranslation(canvasType, canvases.length),
                  count: canvases.length,
                }),
              )
            }

            if (readonlyErrors.length > 0) {
              bulkErrorDialog(
                readonlyErrors,
                t('canvasUI.components.bulkRemoveTags.errorTitle', {
                  entity: getCanvasTypeTranslation(canvasType, readonlyErrors.length),
                  count: readonlyErrors.length,
                }),
              )
            }
          })
          .finally(async () => {
            await onComplete()
            isBulkUpdating.value = false
          })
      },
    })
  }

  async function bulkUpdateDistributionDialog({
    selectedCount,
    isBulkUpdating,
    canvasType,
    selectedRows,
    onComplete,
    title,
    confirmText,
    cancelText,
  }: {
    selectedCount: number
    isBulkUpdating: Ref<boolean>
    canvasType: CanvasType
    selectedRows: Ref<Record<string, boolean>>
    onComplete: () => void | Promise<void>
    title?: string
    confirmText?: string
    cancelText?: string
  }) {
    await fetchDistributionGroups()

    const accessType = ref<'keepAsIs' | 'public' | 'restricted'>('keepAsIs')
    const groupUpdateType = ref<'addNew' | 'replaceAll'>('addNew')
    const selectedGroups = ref<string[]>([])
    const groups = distributionGroups.value.map((group) => {
      const userCount = group.size ?? 0
      return {
        label: `${group.name} (${userCount} ${userCount === 1 ? 'user' : 'users'})`,
        value: group.id,
      }
    })

    const RestrictedContent = defineComponent({
      setup() {
        return () => (
          <Fragment>
            <div class="text-xs text-secondary-lighten-2">
              {t('canvasUI.components.bulkUpdateDistribution.restrictedDescription', {
                entity: getCanvasTypeTranslation(canvasType, 2),
              })}
            </div>
            <div class="mt-4 text-text3">{t('canvasUI.components.bulkUpdateDistribution.distributionGroups')}</div>
            <NSelect
              value={groupUpdateType.value}
              options={[
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.groupOptions.addNew'),
                  value: 'addNew',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.groupOptions.replaceAll'),
                  value: 'replaceAll',
                },
              ]}
              onUpdate:value={(val: 'addNew' | 'replaceAll') => (groupUpdateType.value = val)}
            />
            <div>
              <NSelect
                value={selectedGroups.value}
                options={groups}
                multiple={true}
                filterable={true}
                clearable={true}
                placeholder={t('canvasUI.components.bulkUpdateDistribution.distributionGroups')}
                onUpdate:value={(val: string[]) => (selectedGroups.value = val)}
                filter={(pattern: string, option: SelectOption) => {
                  if (!pattern) return true
                  const label = typeof option.label === 'string' ? option.label : ''
                  return label.toLowerCase().includes(pattern.toLowerCase())
                }}
              />
            </div>
          </Fragment>
        )
      },
    })

    const DialogContent = defineComponent({
      setup() {
        return () => (
          <div class="flex flex-col gap-2">
            <div class="text-text3">{t('canvasUI.components.bulkUpdateDistribution.generalAccess')}</div>
            <NSelect
              value={accessType.value}
              options={[
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.keepAsIs'),
                  value: 'keepAsIs',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.public'),
                  value: 'public',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.restricted'),
                  value: 'restricted',
                },
              ]}
              onUpdate:value={(val: 'keepAsIs' | 'public' | 'restricted') => (accessType.value = val)}
            />
            {accessType.value === 'public' && (
              <div class="text-xs text-secondary-lighten-2">
                {t('canvasUI.components.bulkUpdateDistribution.publicDescription', {
                  entity: getCanvasTypeTranslation(canvasType, 2),
                })}
              </div>
            )}
            {accessType.value === 'restricted' && <RestrictedContent />}
          </div>
        )
      },
    })

    confirmation.create({
      style: { width: '600px', maxWidth: '90vw' },
      hasExit: false,
      title:
        title ??
        t('canvasUI.components.bulkUpdateDistribution.title', {
          count: selectedCount,
          entity: getCanvasTypeTranslation(canvasType, selectedCount),
        }),
      content: () => <DialogContent />,
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: async () => {
        if (accessType.value === 'keepAsIs') return

        if (!coreRestApiAxios?.value) {
          throw new Error('API client is not available')
        }

        if (!pitcherInfo?.value?.instanceId) {
          throw new Error('Instance ID not found')
        }

        isBulkUpdating.value = true
        try {
          const selectedIds = Object.entries(selectedRows.value)
            .filter(([, isSelected]) => isSelected)
            .map(([id]) => id)

          const payload = {
            canvas_ids: selectedIds,
            instance_id: pitcherInfo.value.instanceId,
            access_type: accessType.value as 'public' | 'restricted',
            ...(accessType.value === 'restricted' && {
              ...(selectedGroups.value.length > 0 && {
                target_ids: selectedGroups.value,
              }),
              ...(groupUpdateType.value === 'replaceAll' && {
                type: 'replace',
              }),
            }),
          }

          await coreRestApiAxios.value.post('protected/canvases/distribute', payload)

          toast.success(
            t('canvasUI.components.bulkUpdateDistribution.successMsg', {
              count: selectedCount,
              entity: getCanvasTypeTranslation(canvasType, selectedCount),
            }),
          )
          await onComplete()
        } catch (err) {
          console.error('Failed to update distribution:', err)
          toast.error(t('canvasUI.components.bulkUpdateDistribution.errorMsg'))
        } finally {
          isBulkUpdating.value = false
        }
      },
    })
  }

  async function bulkUpdateFileDistributionDialog({
    selectedCount,
    isBulkUpdating,
    selectedRows,
    onComplete,
    title,
    confirmText,
    cancelText,
  }: {
    selectedCount: number
    isBulkUpdating: Ref<boolean>
    selectedRows: Ref<Record<string, boolean>>
    onComplete: () => void | Promise<void>
    title?: string
    confirmText?: string
    cancelText?: string
  }) {
    await fetchDistributionGroups()

    const selectedIds = getSelectedIds(selectedRows)
    const accessType = ref<'keepAsIs' | 'public' | 'restricted'>('keepAsIs')
    const groupUpdateType = ref<'addNew' | 'replaceAll'>('addNew')
    const selectedGroups = ref<string[]>([])
    const groups = distributionGroups.value.map((group) => {
      const userCount = group.size ?? 0
      return {
        label: `${group.name} (${userCount} ${userCount === 1 ? 'user' : 'users'})`,
        value: group.id,
      }
    })

    const RestrictedContent = defineComponent({
      setup() {
        return () => (
          <Fragment>
            <div class="text-xs text-secondary-lighten-2">
              {t('canvasUI.components.bulkUpdateDistribution.restrictedDescription', {
                entity: getCanvasTypeTranslation('file', 2),
              })}
            </div>
            <div class="mt-4 text-text3">{t('canvasUI.components.bulkUpdateDistribution.distributionGroups')}</div>
            <NSelect
              value={groupUpdateType.value}
              options={[
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.groupOptions.addNew'),
                  value: 'addNew',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.groupOptions.replaceAll'),
                  value: 'replaceAll',
                },
              ]}
              onUpdate:value={(val: 'addNew' | 'replaceAll') => (groupUpdateType.value = val)}
            />
            <div>
              <NSelect
                value={selectedGroups.value}
                options={groups}
                multiple={true}
                filterable={true}
                clearable={true}
                placeholder={t('canvasUI.components.bulkUpdateDistribution.distributionGroups')}
                onUpdate:value={(val: string[]) => (selectedGroups.value = val)}
                filter={(pattern: string, option: SelectOption) => {
                  if (!pattern) return true
                  const label = typeof option.label === 'string' ? option.label : ''
                  return label.toLowerCase().includes(pattern.toLowerCase())
                }}
              />
            </div>
          </Fragment>
        )
      },
    })

    const DialogContent = defineComponent({
      setup() {
        return () => (
          <div class="flex flex-col gap-2">
            <div class="text-text3">{t('canvasUI.components.bulkUpdateDistribution.generalAccess')}</div>
            <NSelect
              value={accessType.value}
              options={[
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.keepAsIs'),
                  value: 'keepAsIs',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.public'),
                  value: 'public',
                },
                {
                  label: t('canvasUI.components.bulkUpdateDistribution.options.restricted'),
                  value: 'restricted',
                },
              ]}
              onUpdate:value={(val: 'keepAsIs' | 'public' | 'restricted') => (accessType.value = val)}
            />
            {accessType.value === 'public' && (
              <div class="text-xs text-secondary-lighten-2">
                {t('canvasUI.components.bulkUpdateDistribution.publicDescription', {
                  entity: getCanvasTypeTranslation('file', 2),
                })}
              </div>
            )}
            {accessType.value === 'restricted' && <RestrictedContent />}
          </div>
        )
      },
    })

    confirmation.create({
      style: { width: '600px', maxWidth: '90vw' },
      hasExit: false,
      title:
        title ??
        t('canvasUI.components.bulkUpdateDistribution.title', {
          count: selectedCount,
          entity: getCanvasTypeTranslation('file', selectedCount),
        }),
      content: () => <DialogContent />,
      confirmText: confirmText ?? t('canvasUI.common.confirm'),
      cancelText: cancelText ?? t('canvasUI.common.cancel'),
      onConfirm: async () => {
        if (accessType.value === 'keepAsIs') return

        if (!coreRestApiAxios?.value) {
          throw new Error('API client is not available')
        }

        if (!pitcherInfo?.value?.instanceId) {
          throw new Error('Instance ID not found')
        }

        isBulkUpdating.value = true
        try {
          const payload = {
            file_ids: selectedIds,
            instance_id: pitcherInfo.value.instanceId,
            access_type: accessType.value as 'public' | 'restricted',
            ...(accessType.value === 'restricted' && {
              ...(selectedGroups.value.length > 0 && {
                target_ids: selectedGroups.value,
              }),
              ...(groupUpdateType.value === 'replaceAll' && {
                type: 'replace',
              }),
            }),
          }

          await coreRestApiAxios.value.post('protected/files/distribute', payload)

          toast.success(
            t('canvasUI.components.bulkUpdateDistribution.successMsg', {
              count: selectedCount,
              entity: getCanvasTypeTranslation('file', selectedCount),
            }),
          )
          await onComplete()
        } catch (err) {
          console.error('Failed to update distribution:', err)
          toast.error(t('canvasUI.components.bulkUpdateDistribution.errorMsg'))
        } finally {
          isBulkUpdating.value = false
        }
      },
    })
  }

  return {
    bulkDeleteDialog,
    bulkUpdateMetadataDialog,
    bulkUpdateFileAttributes,
    bulkAddTagsDialog,
    bulkRemoveTagsDialog,
    bulkUpdateDistributionDialog,
    bulkUpdateFileDistributionDialog,
  }
}
